import { useState, useEffect, useCallback } from 'react'
import { X, Loader2, Clock, GitBranch, Save, RotateCcw, Tag, Bot } from 'lucide-react'
import { scopeVersionsApi } from '@api/client'
import type { ScopeVersion } from '../types'

interface Props {
  dealId: number
  onClose: () => void
  onRestore: (versionId: number) => void
}

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts + 'Z')
    return d.toLocaleString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return ts
  }
}

function versionTypeBadge(v: ScopeVersion) {
  if (v.version_type === 'ai_proposal') {
    const statusColors: Record<string, string> = {
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      accepted: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      modified: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    }
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full border ${statusColors[v.proposal_status || 'pending'] || statusColors.pending}`}>
        <Bot size={10} />
        AI {v.proposal_status || 'pending'}
      </span>
    )
  }

  const isAuto = v.version_label?.startsWith('Auto:')
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full border ${isAuto ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'}`}>
      {isAuto ? <GitBranch size={10} /> : <Save size={10} />}
      {isAuto ? 'Auto' : 'Manual'}
    </span>
  )
}

export function VersionHistoryModal({ dealId, onClose, onRestore }: Props) {
  const [versions, setVersions] = useState<ScopeVersion[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [restoring, setRestoring] = useState<number | null>(null)
  const [confirmRestore, setConfirmRestore] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await scopeVersionsApi.list(dealId)
      if (res.success && res.data) {
        setVersions(Array.isArray(res.data.versions) ? res.data.versions as ScopeVersion[] : [])
        setTotal(res.data.total)
      }
    } catch {
      // silent
    } finally {
      setLoading(false)
    }
  }, [dealId])

  useEffect(() => { load() }, [load])

  const handleRestore = async (versionId: number) => {
    setRestoring(versionId)
    try {
      const res = await scopeVersionsApi.restore(dealId, versionId)
      if (res.success) {
        onRestore(versionId)
        setConfirmRestore(null)
        load()
      }
    } catch {
      // silent
    } finally {
      setRestoring(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)]">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <GitBranch size={18} />
              Version History
            </h3>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              {total} version{total !== 1 ? 's' : ''} saved
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors">
            <X size={18} className="text-[var(--text-muted)]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-[var(--text-muted)]" />
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-12">
              <Clock size={32} className="mx-auto text-[var(--text-muted)] mb-3" />
              <p className="text-sm text-[var(--text-muted)]">No versions saved yet.</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Save a version manually or versions will be created automatically on status changes.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {versions.map((v) => (
                <div key={v.id} className="border border-[var(--border-color)] rounded-lg p-4 hover:bg-[var(--bg-secondary)] transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[var(--text-primary)]">
                          v{v.version_number}
                        </span>
                        {versionTypeBadge(v)}
                        {v.version_label && (
                          <span className="inline-flex items-center gap-1 text-xs text-[var(--text-secondary)]">
                            <Tag size={10} />
                            {v.version_label}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-[var(--text-muted)]">
                        <span>{v.created_by_name || v.created_by || 'Unknown'}</span>
                        <span>{formatTimestamp(v.created_at)}</span>
                        {v.total_hours > 0 && <span>{v.total_hours}h / ${Math.round(v.total_cost).toLocaleString()}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {confirmRestore === v.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            className="px-2 py-1 text-xs rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 transition-colors disabled:opacity-50"
                            onClick={() => handleRestore(v.id)}
                            disabled={restoring === v.id}
                          >
                            {restoring === v.id ? 'Restoring...' : 'Confirm'}
                          </button>
                          <button
                            className="px-2 py-1 text-xs rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                            onClick={() => setConfirmRestore(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="p-1.5 hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors"
                          onClick={() => setConfirmRestore(v.id)}
                          title="Restore this version"
                        >
                          <RotateCcw size={14} className="text-[var(--text-muted)]" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
